'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { SelectedEmailInfo } from './selected-email-chips'
import {
  readStored,
  readUnsavedList,
  writeAttachmentList,
} from './unsaved-attachment-lists'

type Scope = {
  orgId: string
  brandId: string | undefined
  chatId: string | null
}

function isSameScope(a: Scope, b: Scope) {
  return a.orgId === b.orgId && a.brandId === b.brandId && a.chatId === b.chatId
}

export const ATTACHED_EMAILS_PREFIX = 'canvas-chat-attached-emails-v1'

/**
 * Build the per-(orgId, brandId, chatId) localStorage key that holds the
 * "attached to chat composer" emails for the canvas chat panel.
 *
 * The key is intentionally scoped per chat tab so users can stage
 * different attachments in parallel chats. `brandId` falls back to
 * `'none'` to mirror the existing pattern in
 * `components/canvas/use-canvas-chats-utils.ts`.
 */
export function getAttachedEmailsKey(
  orgId: string,
  brandId: string | undefined,
  chatId: string
): string {
  const brandSuffix = brandId ?? 'none'
  return `${ATTACHED_EMAILS_PREFIX}:${orgId}:${brandSuffix}:${chatId}`
}

function isValidVersion(value: unknown): value is number | 'latest' {
  if (value === 'latest') {
    return true
  }
  if (typeof value !== 'number') {
    return false
  }
  return Number.isInteger(value) && value >= 0
}

function isValidStored(value: unknown): value is SelectedEmailInfo {
  if (!value || typeof value !== 'object') {
    return false
  }
  const candidate = value as {
    emailId?: unknown
    title?: unknown
    imagePngUrl?: unknown
    version?: unknown
    isRemixSource?: unknown
    remixSourceFlowSlug?: unknown
    subtitle?: unknown
    thumbnails?: unknown
  }
  if (
    typeof candidate.emailId !== 'string' ||
    candidate.emailId.length === 0 ||
    typeof candidate.title !== 'string' ||
    candidate.title.length === 0
  ) {
    return false
  }
  if (
    candidate.imagePngUrl !== undefined &&
    candidate.imagePngUrl !== null &&
    typeof candidate.imagePngUrl !== 'string'
  ) {
    return false
  }
  if (candidate.version !== undefined && !isValidVersion(candidate.version)) {
    return false
  }
  if (
    candidate.isRemixSource !== undefined &&
    typeof candidate.isRemixSource !== 'boolean'
  ) {
    return false
  }
  if (
    candidate.remixSourceFlowSlug !== undefined &&
    (typeof candidate.remixSourceFlowSlug !== 'string' ||
      candidate.remixSourceFlowSlug.length === 0)
  ) {
    return false
  }
  if (
    candidate.subtitle !== undefined &&
    typeof candidate.subtitle !== 'string'
  ) {
    return false
  }
  if (
    candidate.thumbnails !== undefined &&
    !(
      Array.isArray(candidate.thumbnails) &&
      candidate.thumbnails.every(
        (url) => typeof url === 'string' && url.length > 0
      )
    )
  ) {
    return false
  }
  return true
}

function normalizeForStorage(email: SelectedEmailInfo): SelectedEmailInfo {
  return {
    emailId: email.emailId,
    title: email.title,
    ...(email.imagePngUrl !== undefined
      ? { imagePngUrl: email.imagePngUrl }
      : {}),
    // Only persist a numeric version pin. `'latest'` is the default and
    // is omitted to keep the persisted shape minimal.
    ...(typeof email.version === 'number' ? { version: email.version } : {}),
    // Owning automationId for /automations-canvas attaches; mirrors
    // the existing `automationId` field on `SelectedEmailMetadata`.
    ...(typeof (email as { automationId?: unknown }).automationId ===
      'string' &&
    ((email as { automationId?: string }).automationId?.length ?? 0) > 0
      ? { automationId: (email as { automationId?: string }).automationId }
      : {}),
    // Template-page attach marker — drives the per-turn
    // `remixSourceEmailId` request field in the chat thread. Persisted
    // here (chip lattice only); never enters the message data part.
    ...(email.isRemixSource ? { isRemixSource: true } : {}),
    // Flow-page attach marker — drives the per-turn `remixSourceFlowSlug`
    // request field and keeps the chip out of the selected emails.
    ...(email.remixSourceFlowSlug
      ? { remixSourceFlowSlug: email.remixSourceFlowSlug }
      : {}),
    // What the chip says under its title and the cards it fans out — set
    // by the template and flow pages, shown on the composer chip and
    // carried onto the sent turn's remix-source card.
    ...(email.subtitle ? { subtitle: email.subtitle } : {}),
    ...(email.thumbnails && email.thumbnails.length > 0
      ? { thumbnails: [...email.thumbnails] }
      : {}),
  }
}

/** A valid chip's identity, to match another tab's changes by. */
function emailIdOf(entry: unknown): string | undefined {
  return isValidStored(entry) ? entry.emailId : undefined
}

export function readStoredAttachedEmails(
  orgId: string,
  brandId: string | undefined,
  chatId: string
): Array<SelectedEmailInfo> {
  if (typeof window === 'undefined') {
    return []
  }
  const key = getAttachedEmailsKey(orgId, brandId, chatId)
  const unsaved = readUnsavedList<SelectedEmailInfo>(key)
  if (unsaved) {
    return unsaved
  }
  try {
    const raw = readStored(key)
    if (!raw) {
      return []
    }
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter(isValidStored)
  } catch {
    return []
  }
}

export function writeStoredAttachedEmails(
  orgId: string,
  brandId: string | undefined,
  chatId: string,
  emails: ReadonlyArray<SelectedEmailInfo>
): void {
  if (typeof window === 'undefined') {
    return
  }
  // Storage refusing the write (quota / disabled storage) keeps the list in
  // memory for this page, which the read above prefers.
  writeAttachmentList(
    getAttachedEmailsKey(orgId, brandId, chatId),
    emails.map(normalizeForStorage),
    emailIdOf
  )
}

export function clearStoredAttachedEmails(
  orgId: string,
  brandId: string | undefined,
  chatId: string
): void {
  if (typeof window === 'undefined') {
    return
  }
  writeAttachmentList(
    getAttachedEmailsKey(orgId, brandId, chatId),
    [],
    emailIdOf
  )
}

/**
 * Upsert `email` into `current`. New emails append; an existing entry is
 * MERGED with the incoming fields and moved to the end (re-attaching
 * represents the freshest intent, and the chat thread's "last flagged
 * chip wins" remix derivation relies on recency ordering).
 *
 * Merging — rather than the previous skip-on-duplicate — matters for the
 * templates page: visiting a template whose email the user already staged
 * from the canvas must still stamp `isRemixSource` onto the existing
 * chip. `normalizeForStorage` omits unset/falsy fields, so a plain
 * canvas re-attach can never CLEAR an existing flag or version pin.
 *
 * Returns `current` (same reference) when the upsert is a no-op so
 * callers can skip persisting.
 */
export function upsertAttachedEmail(
  current: ReadonlyArray<SelectedEmailInfo>,
  email: SelectedEmailInfo
): ReadonlyArray<SelectedEmailInfo> {
  const incoming = normalizeForStorage(email)
  const existing = current.find((e) => e.emailId === email.emailId)
  if (!existing) {
    return [...current, incoming]
  }

  const merged = { ...existing, ...incoming }
  const isUnchanged =
    current[current.length - 1] === existing &&
    JSON.stringify(merged) === JSON.stringify(existing)
  if (isUnchanged) {
    return current
  }

  return [...current.filter((e) => e.emailId !== email.emailId), merged]
}

/**
 * Stage `email` as THE remix source of `current` — the templates page's
 * "at most one remix template per chat" contract.
 *
 * Every OTHER chip carrying `isRemixSource` is dropped: the chat thread
 * derives a single `remixSourceEmailId` from the most recently flagged
 * chip, so a second staged template would silently lose to attach order
 * (and the orchestrator's REMIX MODE block takes exactly one source).
 * Plain (canvas-attached) chips are left untouched.
 *
 * Returns `current` (same reference) when the list already satisfies this
 * so callers can skip persisting.
 */
export function setRemixSourceEmail(
  current: ReadonlyArray<SelectedEmailInfo>,
  email: SelectedEmailInfo
): ReadonlyArray<SelectedEmailInfo> {
  const pruned = current.filter(
    (e) => e.emailId === email.emailId || !e.isRemixSource
  )
  const next = upsertAttachedEmail(pruned, { ...email, isRemixSource: true })
  const isUnchanged = next === pruned && pruned.length === current.length
  return isUnchanged ? current : next
}

/**
 * Undo {@link setRemixSourceEmail} for `emailId` — the templates page's
 * leaving-the-page cleanup (the chip belongs to "you are looking at this
 * template", not to the conversation).
 *
 * Only a chip that is STILL flagged `isRemixSource` is touched, so a chip
 * the user re-staged from the canvas in the meantime survives.
 *
 * `shouldKeepChip` strips the flag but keeps the chip: the email was
 * already attached (from the emails canvas) BEFORE the template page
 * stamped it, so removing it outright would silently drop an attachment
 * the user made themselves.
 */
export function clearRemixSourceEmail(
  current: ReadonlyArray<SelectedEmailInfo>,
  emailId: string,
  shouldKeepChip = false
): ReadonlyArray<SelectedEmailInfo> {
  const index = current.findIndex(
    (e) => e.emailId === emailId && e.isRemixSource === true
  )
  if (index === -1) {
    return current
  }
  if (!shouldKeepChip) {
    return current.filter((_, i) => i !== index)
  }
  const next = [...current]
  // `normalizeForStorage` omits a falsy flag, so this is the drop —
  // routed through the one normalizer so the surviving chip keeps exactly
  // the fields the lattice persists.
  next[index] = normalizeForStorage({
    ...current[index]!,
    isRemixSource: false,
  })
  return next
}

/**
 * Drop EVERY remix-source chip. Only the templates page sets the flag, and
 * only for as long as a template is on screen — so any chip still carrying
 * it when the user lands on a template again is a leftover from a visit
 * that ended without React cleanup (hard reload, tab close, crash).
 */
export function clearAllRemixSourceEmails(
  current: ReadonlyArray<SelectedEmailInfo>
): ReadonlyArray<SelectedEmailInfo> {
  const next = current.filter((e) => !e.isRemixSource)
  return next.length === current.length ? current : next
}

/**
 * Move the chips staged on `source` onto `target` — "New chat" carrying the
 * composer's attachments. Remix-source chips stay behind: they belong to
 * the template or flow page on screen, which re-stages its chip on
 * whichever chat becomes active (and would otherwise mistake the moved chip
 * for one the user attached, keeping it after they leave the page).
 *
 * Returns both lists as they are when nothing moves.
 */
export function carryStagedEmails(
  source: ReadonlyArray<SelectedEmailInfo>,
  target: ReadonlyArray<SelectedEmailInfo>
): {
  source: ReadonlyArray<SelectedEmailInfo>
  target: ReadonlyArray<SelectedEmailInfo>
} {
  const moving = source.filter((e) => !e.isRemixSource)
  if (moving.length === 0) {
    return { source, target }
  }
  return {
    source: source.filter((e) => e.isRemixSource),
    target: moving.reduce(upsertAttachedEmail, target),
  }
}

export type UseChatAttachedEmailsReturn = {
  attachedEmails: Array<SelectedEmailInfo>
  isAttached: (emailId: string) => boolean
  attachEmail: (email: SelectedEmailInfo) => void
  attachManyEmails: (emails: ReadonlyArray<SelectedEmailInfo>) => void
  detachEmail: (emailId: string) => void
  clearAttached: () => void
  /**
   * Drop entries whose emailId is not in `validEmailIds`. No-op when the
   * set is empty (treated as "not yet hydrated; trust everything").
   */
  pruneAgainst: (validEmailIds: ReadonlySet<string>) => void
  /**
   * Stage `email` as the single remix source of `targetChatId` — which
   * may be ANY open chat, not just the active one (the templates page
   * re-stamps whichever chat the user switches to while the template is
   * on screen). Returns true when the email was already attached to that
   * chat beforehand, so the caller can restore rather than delete when it
   * undoes the stamp.
   */
  setRemixSource: (targetChatId: string, email: SelectedEmailInfo) => boolean
  /** Undo `setRemixSource` for `emailId` on `targetChatId`. */
  clearRemixSource: (
    targetChatId: string,
    emailId: string,
    shouldKeepChip?: boolean
  ) => void
  /**
   * Drop every remix-source chip from `targetChatId` — the self-heal for
   * a template visit that ended without cleanup (hard reload).
   */
  clearAllRemixSources: (targetChatId: string) => void
  /**
   * Move the live chat's staged chips onto `targetChatId` (see
   * {@link carryStagedEmails}) — "New chat" calls it with the chat it just
   * opened.
   */
  moveStagedTo: (targetChatId: string) => void
}

/**
 * Manage the per-chat-tab "attached to chat composer" emails set. Source
 * of truth is in-memory state, mirrored to a per-`(orgId, brandId, chatId)`
 * localStorage key so the staged attachments survive reloads. Cleared
 * either explicitly via `clearAttached()` (called after the user submits
 * a message) or by removal of the underlying email from the canvas (via
 * `pruneAgainst`).
 *
 * `chatId === null` is treated as "no active chat"; the hook returns an
 * empty list and no-ops on writes. This matches the canvas chat panel
 * which only renders the thread when a chat tab is active.
 */
export function useChatAttachedEmails(args: {
  orgId: string
  brandId?: string
  chatId: string | null
}): UseChatAttachedEmailsReturn {
  const { orgId, brandId, chatId } = args

  // Initialize from storage synchronously so the chips render on first
  // paint after a reload. Re-hydration on scope (orgId/brandId/chatId)
  // change uses the React 19 "derive state from changing prop" pattern
  // (compare scope on render, call `setState` directly during render —
  // not inside an effect) so the lint rule against `setState` in
  // effects is happy and we avoid a wasted commit + cascading render.
  const [attachedEmails, setAttachedEmails] = useState<
    Array<SelectedEmailInfo>
  >(() => (chatId ? readStoredAttachedEmails(orgId, brandId, chatId) : []))
  const [trackedScope, setTrackedScope] = useState<Scope>({
    orgId,
    brandId,
    chatId,
  })
  // The read prefers a list storage refused (see
  // `unsaved-attachment-lists.ts`), so a chat — including one `moveStagedTo`
  // just filled — reads back what it holds with storage full or disabled.
  if (!isSameScope(trackedScope, { orgId, brandId, chatId })) {
    setTrackedScope({ orgId, brandId, chatId })
    setAttachedEmails(
      chatId ? readStoredAttachedEmails(orgId, brandId, chatId) : []
    )
  }

  const persist = useCallback(
    (next: Array<SelectedEmailInfo>) => {
      setAttachedEmails(next)
      if (chatId) {
        writeStoredAttachedEmails(orgId, brandId, chatId, next)
      }
    },
    [orgId, brandId, chatId]
  )

  const attachedEmailsRef = useRef(attachedEmails)
  useEffect(() => {
    attachedEmailsRef.current = attachedEmails
  }, [attachedEmails])

  const isAttached = useCallback(
    (emailId: string) =>
      attachedEmailsRef.current.some((e) => e.emailId === emailId),
    []
  )

  const attachEmail = useCallback(
    (email: SelectedEmailInfo) => {
      if (!chatId) {
        return
      }
      const current = attachedEmailsRef.current
      const next = upsertAttachedEmail(current, email)
      if (next === current) {
        return
      }
      persist(next as Array<SelectedEmailInfo>)
    },
    [chatId, persist]
  )

  const attachManyEmails = useCallback(
    (emails: ReadonlyArray<SelectedEmailInfo>) => {
      if (!chatId) {
        return
      }
      const current = attachedEmailsRef.current
      const existingIds = new Set(current.map((e) => e.emailId))
      const additions: Array<SelectedEmailInfo> = []
      for (const email of emails) {
        if (!email.emailId || existingIds.has(email.emailId)) {
          continue
        }
        existingIds.add(email.emailId)
        additions.push(normalizeForStorage(email))
      }
      if (additions.length === 0) {
        return
      }
      persist([...current, ...additions])
    },
    [chatId, persist]
  )

  const detachEmail = useCallback(
    (emailId: string) => {
      if (!chatId) {
        return
      }
      const current = attachedEmailsRef.current
      if (!current.some((e) => e.emailId === emailId)) {
        return
      }
      const next = current.filter((e) => e.emailId !== emailId)
      persist(next)
    },
    [chatId, persist]
  )

  const clearAttached = useCallback(() => {
    if (!chatId) {
      return
    }
    if (attachedEmailsRef.current.length === 0) {
      return
    }
    persist([])
  }, [chatId, persist])

  /**
   * Apply `mutate` to ANY chat's lattice, live or background. Returns the
   * list as it was BEFORE the mutation.
   *
   * The base list is always read from STORAGE, never from
   * `attachedEmailsRef` — even for the live chat. Every `persist` writes
   * both state and storage synchronously, so storage is always current,
   * whereas the ref is synced in an effect on THIS hook (which lives in
   * the provider). Child effects run before parent effects, so a caller
   * like the templates page's chat-switch stamp fires in the same commit
   * that re-hydrated the scope and would read the PREVIOUS chat's list —
   * then write it into the newly active chat, clobbering its attachments.
   */
  const mutateChat = useCallback(
    (
      targetChatId: string,
      mutate: (
        current: ReadonlyArray<SelectedEmailInfo>
      ) => ReadonlyArray<SelectedEmailInfo>
    ): ReadonlyArray<SelectedEmailInfo> => {
      const current = readStoredAttachedEmails(orgId, brandId, targetChatId)
      const next = mutate(current)
      if (next === current) {
        return current
      }
      // Live chat goes through `persist` so the composer repaints; a
      // background tab only needs its storage copy (the hook re-hydrates
      // from it when the scope flips to that chat).
      if (targetChatId === chatId) {
        persist(next as Array<SelectedEmailInfo>)
      } else {
        writeStoredAttachedEmails(orgId, brandId, targetChatId, next)
      }
      return current
    },
    [chatId, orgId, brandId, persist]
  )

  const setRemixSource = useCallback(
    (targetChatId: string, email: SelectedEmailInfo) => {
      const previous = mutateChat(targetChatId, (current) =>
        setRemixSourceEmail(current, email)
      )
      return previous.some((e) => e.emailId === email.emailId)
    },
    [mutateChat]
  )

  const clearRemixSource = useCallback(
    (targetChatId: string, emailId: string, shouldKeepChip = false) => {
      mutateChat(targetChatId, (current) =>
        clearRemixSourceEmail(current, emailId, shouldKeepChip)
      )
    },
    [mutateChat]
  )

  const clearAllRemixSources = useCallback(
    (targetChatId: string) => {
      mutateChat(targetChatId, clearAllRemixSourceEmails)
    },
    [mutateChat]
  )

  const moveStagedTo = useCallback(
    (targetChatId: string) => {
      if (!chatId || targetChatId === chatId) {
        return
      }
      // The live list, not storage: a click handler runs after every effect
      // of the last commit, so the ref is current, and storage may be
      // unavailable.
      const source = attachedEmailsRef.current
      const next = carryStagedEmails(
        source,
        readStoredAttachedEmails(orgId, brandId, targetChatId)
      )
      if (next.source === source) {
        return
      }
      writeStoredAttachedEmails(orgId, brandId, targetChatId, next.target)
      persist(next.source as Array<SelectedEmailInfo>)
    },
    [chatId, orgId, brandId, persist]
  )

  const pruneAgainst = useCallback(
    (validEmailIds: ReadonlySet<string>) => {
      if (!chatId) {
        return
      }
      // Treat empty set as "not yet hydrated"; do not prune.
      if (validEmailIds.size === 0) {
        return
      }
      const current = attachedEmailsRef.current
      const next = current.filter((e) => validEmailIds.has(e.emailId))
      if (next.length === current.length) {
        return
      }
      persist(next)
    },
    [chatId, persist]
  )

  // Memoize the return shape. Without this, every render produces a
  // new object identity, which propagates through the workspace shell's
  // `CanvasLayoutContext` value (the shell's own `useMemo` re-runs
  // because one of its deps changed reference), forcing every emails
  // canvas consumer to re-render on every shell render. Each individual
  // field is already stable (state value or memoized callback), so the
  // only thing to stabilize is the wrapping object.
  return useMemo(
    () => ({
      attachedEmails,
      isAttached,
      attachEmail,
      attachManyEmails,
      detachEmail,
      clearAttached,
      pruneAgainst,
      setRemixSource,
      clearRemixSource,
      clearAllRemixSources,
      moveStagedTo,
    }),
    [
      attachedEmails,
      isAttached,
      attachEmail,
      attachManyEmails,
      detachEmail,
      clearAttached,
      pruneAgainst,
      setRemixSource,
      clearRemixSource,
      clearAllRemixSources,
      moveStagedTo,
    ]
  )
}
